/**
 * L6b: Reiter „Abzurechnen“ – was ist offen, eine Zeile je Auftrag.
 *
 * Zwei Blöcke nach Abrechnungsart des Auftrags (Auftrag → Kunde → Firmenstandard):
 *  - Monatsabrechnung (gewählter Monat): Aufträge mit monatlicher (oder quartalsweiser) Abrechnung, nur die
 *    Buchungen des gewählten Monats; Reste aus früheren Monaten zusammengeklappt darunter.
 *  - Nach Leistung: Aufträge, die abgerechnet werden, wenn Teilaufgaben fertig sind (Regelbetreuung).
 *    Eine Zeile je Auftrag mit allen offenen Buchungen bis zum Stichtag, Tätigkeiten als Übersicht;
 *    in „Auswahl“ lassen sich fertige Teilaufgaben je Tätigkeit herausgreifen.
 * Keine Aufgabenverfolgung: CTIme merkt sich nichts, was nicht angelegt wurde, bleibt offen.
 * Daten: GET /api/billing/suggestions?month=YYYY-MM (schreibt nichts).
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, AlertTriangle, SlidersHorizontal, FilePlus2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '../ui/sheet';
import { formatCurrency, formatDate, getStatusBadge, handleApiError } from './billingUtils';

const REFERENCE_LABELS = { po: 'Bestellnummer', contract: 'Vertragsnummer', quote: 'Angebotsnummer' };
const GRID = 'grid-cols-[2.6fr_1.3fr_0.7fr_0.7fr_1fr_1.4fr_2fr]';

// Standard: ab dem 25. der laufende Monat, sonst der Vormonat
export const defaultBillingMonth = (today = new Date()) => {
  const d = new Date(today.getFullYear(), today.getMonth() - (today.getDate() >= 25 ? 0 : 1), 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const shiftMonth = (ym, delta) => {
  const d = new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const monthLabel = (ym) => `${MONTH_NAMES[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
// G25: Monatswechsel – kurz warten, bis nicht mehr weitergeklickt wird; gemerkte Monate bleiben so lange gültig
const MONTH_SWITCH_DELAY_MS = 400;
const CACHE_MAX_AGE_MS = 5 * 60 * 1000;
const hoursText = (n) => `${(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const LAMP = {
  fehler: { dot: 'bg-red-500', text: 'text-red-700 dark:text-red-300' },
  warnung: { dot: 'bg-amber-500', text: 'text-foreground' },
};

export default function BillingSuggestions({ api, canCreate, onOpenInvoice }) {
  const [month, setMonth] = useState(defaultBillingMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [carryoverOpen, setCarryoverOpen] = useState(false);
  const [references, setReferences] = useState({}); // key -> eingegebene Pflichtreferenz
  const [creating, setCreating] = useState(null);
  const [panel, setPanel] = useState(null); // { suggestion, entries:Set, mileage:Set }

  // G25: Monatswechsel ohne Rennen zwischen Anfragen.
  // - Die Anzeige springt sofort auf den gewählten Monat; alte Daten werden ausgegraut, bis die neuen da sind.
  // - Geladen wird erst, wenn 400 ms nicht weitergeklickt wurde (Oktober → Februar = eine Anfrage, nicht vier).
  // - Nur die Antwort zum zuletzt gewählten Monat wird angezeigt; ältere Antworten werden verworfen.
  // - Geladene Monate werden 5 Minuten gemerkt; danach wird der Folgemonat im Hintergrund vorgeladen.
  const cacheRef = useRef(new Map()); // month -> { data, at }
  const inflightRef = useRef(new Map()); // month -> Promise
  const monthRef = useRef(month);
  monthRef.current = month;
  const firstLoadRef = useRef(true);

  const fetchMonth = useCallback((m) => {
    if (inflightRef.current.has(m)) return inflightRef.current.get(m);
    const p = api.get(`/billing/suggestions?month=${m}`)
      .then((response) => {
        cacheRef.current.set(m, { data: response.data, at: Date.now() });
        return response.data;
      })
      .finally(() => inflightRef.current.delete(m));
    inflightRef.current.set(m, p);
    return p;
  }, [api]);

  const prefetch = useCallback((m) => {
    const hit = cacheRef.current.get(m);
    if (hit && Date.now() - hit.at < CACHE_MAX_AGE_MS) return;
    fetchMonth(m).catch(() => {}); // still im Hintergrund; Fehler zeigt erst das echte Laden
  }, [fetchMonth]);

  const load = useCallback(async ({ force = false } = {}) => {
    const m = monthRef.current;
    if (force) cacheRef.current.clear();
    const hit = cacheRef.current.get(m);
    if (hit && Date.now() - hit.at < CACHE_MAX_AGE_MS) {
      setData(hit.data);
      setLoading(false);
      prefetch(shiftMonth(m, 1));
      return;
    }
    setLoading(true);
    try {
      const result = await fetchMonth(m);
      if (monthRef.current !== m) return; // inzwischen anderer Monat gewählt – verwerfen
      setData(result);
      prefetch(shiftMonth(m, 1));
    } catch (error) {
      if (monthRef.current !== m) return;
      setData(null);
      handleApiError(error);
    } finally {
      if (monthRef.current === m) setLoading(false);
    }
  }, [fetchMonth, prefetch]);

  useEffect(() => {
    const hit = cacheRef.current.get(month);
    const fresh = hit && Date.now() - hit.at < CACHE_MAX_AGE_MS;
    // erster Aufruf und gemerkte Monate sofort, sonst kurz warten, ob weitergeklickt wird
    const delay = firstLoadRef.current || fresh ? 0 : MONTH_SWITCH_DELAY_MS;
    firstLoadRef.current = false;
    if (!fresh) setLoading(true);
    const timer = setTimeout(() => load(), delay);
    return () => clearTimeout(timer);
  }, [month, load]);

  // Daten eines anderen Monats werden noch angezeigt, während der gewählte lädt
  const showingOtherMonth = Boolean(data && data.month !== month);

  const referenceNeeded = (s) => s.reference?.required && !s.reference?.value;
  const referenceInput = (s) => (references[s.key] || '').trim();

  const createDraft = async (s, entryIds, mileageIds) => {
    if (referenceNeeded(s) && !referenceInput(s)) {
      setExpanded(s.key);
      toast.error(`${REFERENCE_LABELS[s.reference.type] || 'Referenz'} bitte zuerst eintragen`);
      return;
    }
    setCreating(s.key);
    try {
      const payload = {
        customer_id: s.customer_id,
        project_entry_ids: entryIds,
        mileage_entry_ids: mileageIds,
      };
      if (s.mode !== 'none') {
        payload.period_start = s.period_start;
        payload.period_end = s.period_end;
      } else {
        // nach Leistung: Leistungszeitraum = erste bis letzte gewählte Buchung
        const chosen = new Set([...entryIds, ...mileageIds]);
        const dates = [...s.entries, ...s.mileage].filter((x) => chosen.has(x.id)).map((x) => x.date).filter(Boolean).sort();
        if (dates.length) {
          payload.period_start = dates[0];
          payload.period_end = dates[dates.length - 1];
        }
      }
      if (referenceNeeded(s)) payload.reference_value = referenceInput(s);
      const response = await api.post('/billing/invoices/generate-selected', payload);
      const invoice = response.data.invoice;
      toast.success(`Rechnungsentwurf erstellt: ${invoice.draft_reference}`);
      cacheRef.current.clear();
      setPanel(null);
      onOpenInvoice(invoice.id);
    } catch (error) {
      handleApiError(error);
      load({ force: true });
    } finally {
      setCreating(null);
    }
  };

  const openPanel = (s) => setPanel({
    suggestion: s,
    entries: new Set(s.entries.map((e) => e.id)),
    mileage: new Set(s.mileage.map((m) => m.id)),
  });
  const togglePanel = (kind, id) => setPanel((p) => {
    const next = new Set(p[kind]);
    if (next.has(id)) next.delete(id); else next.add(id);
    return { ...p, [kind]: next };
  });
  const toggleGroup = (ids, allOn) => setPanel((p) => {
    const next = new Set(p.entries);
    ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
    return { ...p, entries: next };
  });

  const summary = data?.summary;
  const progress = summary && summary.items_total > 0 ? Math.round((summary.items_done / summary.items_total) * 100) : 0;
  const suggestions = useMemo(() => data?.suggestions || [], [data]);
  const invoices = data?.invoices || [];

  const periodic = suggestions.filter((s) => s.mode !== 'none');
  const monthRows = periodic.filter((s) => !s.carryover);
  const carryoverRows = periodic.filter((s) => s.carryover);
  const completionRows = suggestions.filter((s) => s.mode === 'none');

  // Buchungen der Seitenleiste nach Tätigkeit gruppieren (Teilaufgaben herausgreifen)
  const panelGroups = useMemo(() => {
    if (!panel) return [];
    const map = new Map();
    panel.suggestion.entries.forEach((e) => {
      const key = e.activity || 'ohne Tätigkeit';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return [...map.entries()].map(([label, entries]) => ({ label, entries }));
  }, [panel]);

  const renderRow = (s) => {
    const errors = s.findings.filter((f) => f.severity === 'fehler').length;
    const warnings = s.findings.length - errors;
    const isOpen = expanded === s.key;
    const lamp = s.status === 'running'
      ? { dot: 'bg-zinc-400', label: 'Noch nicht fällig' }
      : errors > 0
        ? { dot: 'bg-red-500', label: `${errors} Fehler` }
        : warnings > 0
          ? { dot: 'bg-amber-500', label: `${warnings} Hinweis${warnings > 1 ? 'e' : ''}` }
          : { dot: 'bg-emerald-500', label: 'Bereit' };
    const needsRef = referenceNeeded(s);
    const canDraft = canCreate && s.status === 'ready' && (!needsRef || referenceInput(s)) && !loading && !showingOtherMonth;
    const activities = (s.activities || []).filter((a) => a.hours > 0 || a.days > 0);
    return (
      <div key={s.key} className={`border-b border-border last:border-b-0 ${s.status === 'running' ? 'opacity-60' : ''}`} data-testid={`suggestion-${s.key}`}>
        <div className={`grid ${GRID} gap-3 px-4 py-3 items-center`}>
          <div className="min-w-0">
            <p className="font-medium text-foreground truncate">{s.customer_name}</p>
            <p className="text-sm text-foreground truncate">{s.label}</p>
            <p className="text-xs text-muted-foreground">
              {s.mode === 'none'
                ? `Buchungen ${formatDate(s.period_start)} – ${formatDate(s.period_end)}`
                : s.period_text}
            </p>
            {s.mode === 'none' && activities.length > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {activities.map((a) => `${a.label} ${a.days ? plural(a.days, 'Tag', 'Tage') : hoursText(a.hours)}`).join(' · ')}
              </p>
            )}
          </div>
          <div className="font-mono text-sm">{hoursText(s.hours_recorded)} / {hoursText(s.hours_billed)}</div>
          <div className="font-mono text-sm">{s.daily_count || '–'}</div>
          <div className="font-mono text-sm">{s.km ? s.km.toLocaleString('de-DE') : '–'}</div>
          <div className="font-mono text-sm font-medium">{s.status === 'blocked' && !s.net_total ? '–' : formatCurrency(s.net_total)}</div>
          <div>
            <button
              type="button"
              onClick={() => setExpanded(isOpen ? null : s.key)}
              className="inline-flex items-center gap-2 text-sm min-h-[32px]"
              aria-expanded={isOpen}
            >
              <span className={`w-2.5 h-2.5 rounded-full ${lamp.dot}`} />
              {lamp.label}
              {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
            </button>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {canCreate && s.status !== 'running' && (
              <>
                <Button type="button" size="sm" disabled={!canDraft || creating === s.key}
                  onClick={() => createDraft(s, s.entries.map((e) => e.id), s.mileage.map((m) => m.id))}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  title={s.status === 'blocked' ? 'Erst die Fehler beheben' : needsRef && !referenceInput(s) ? 'Pflichtreferenz eintragen (Bereitschaft aufklappen)' : s.mode === 'none' ? 'Alle offenen Buchungen dieses Auftrags' : undefined}>
                  <FilePlus2 className="w-4 h-4 mr-2" />
                  {creating === s.key ? 'Erstelle...' : s.mode === 'none' ? 'Alles anlegen' : 'Entwurf anlegen'}
                </Button>
                <Button type="button" size="sm" variant={s.mode === 'none' ? 'secondary' : 'outline'} className={s.mode === 'none' ? '' : 'border-border'} onClick={() => openPanel(s)} disabled={s.status === 'blocked'}
                  title={s.mode === 'none' ? 'Fertige Teilaufgaben nach Tätigkeit herausgreifen' : 'Einzelne Buchungen abwählen'}>
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  {s.mode === 'none' ? 'Teilaufgaben wählen' : 'Auswahl'}
                </Button>
              </>
            )}
          </div>
        </div>
        {(isOpen || (needsRef && s.status === 'ready')) && (
          <div className="px-4 pb-3 space-y-2">
            {isOpen && (s.findings.length > 0 ? (
              <ul className="bg-muted/40 border border-border rounded-md p-3 space-y-1 text-sm">
                {s.findings.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${LAMP[f.severity]?.dot || 'bg-zinc-400'}`} />
                    <span className={LAMP[f.severity]?.text || 'text-muted-foreground'}>{f.text}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground px-1">
                {s.status === 'running' ? 'Der Abrechnungszeitraum läuft noch.' : `Keine Befunde · ${plural(s.position_count, 'Position', 'Positionen')} · ${plural(s.entries.length, 'Buchung', 'Buchungen')}${s.mileage.length ? ` · ${plural(s.mileage.length, 'Fahrt', 'Fahrten')}` : ''}`}
              </p>
            ))}
            {needsRef && s.status === 'ready' && canCreate && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">{REFERENCE_LABELS[s.reference.type] || 'Referenz'} (Pflicht):</span>
                <Input
                  value={references[s.key] || ''}
                  onChange={(e) => setReferences({ ...references, [s.key]: e.target.value })}
                  placeholder="Erforderlich"
                  className="h-8 w-56 bg-input border-border text-foreground"
                  aria-label={`${REFERENCE_LABELS[s.reference.type] || 'Referenz'} für ${s.customer_name}`}
                />
                <span className="text-xs text-muted-foreground">wird in den Entwurf übernommen und kann dort geändert werden</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const tableHead = (
    <div className={`grid ${GRID} gap-3 px-4 py-2 text-xs text-muted-foreground border-b border-border`}>
      <div>Kunde · Auftrag</div><div>Stunden erfasst / abger.</div><div>Tagessätze</div><div>km</div><div>Netto</div><div>Bereitschaft</div><div className="text-right">Aktion</div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Monat und Fortschritt */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="icon" aria-label="Vormonat" onClick={() => setMonth(shiftMonth(month, -1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-[150px] text-center font-semibold text-foreground" aria-live="polite">{monthLabel(month)}</div>
          <Button type="button" variant="outline" size="icon" aria-label="Folgemonat" onClick={() => setMonth(shiftMonth(month, 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <span className="text-xs text-muted-foreground">Stichtag: Buchungen bis Monatsende</span>
        <Button type="button" variant="ghost" size="sm" onClick={() => load({ force: true })} disabled={loading} className="text-muted-foreground">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* G25: Ladehinweis beim Monatswechsel – alte Daten bleiben sichtbar, aber ausgegraut */}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" data-testid="suggestions-loading">
          <RefreshCw className="w-4 h-4 animate-spin" />
          {monthLabel(month)} wird geladen …
        </div>
      )}

      <div
        className={`space-y-4 transition-opacity ${showingOtherMonth ? 'opacity-40 pointer-events-none select-none' : ''}`}
        aria-busy={loading}
        data-testid="suggestions-content"
      >
        {summary && (
          <div className="bg-card/50 border border-border rounded-lg px-4 py-3 flex flex-wrap items-center gap-4">
            <div className="font-medium text-foreground">{summary.items_done} von {summary.items_total} Aufträgen abgerechnet</div>
            <div className="flex-1 min-w-[160px] h-2 rounded bg-muted overflow-hidden">
              <div className="h-2 bg-emerald-500" style={{ width: `${progress}%` }} />
            </div>
            <div className="text-sm text-muted-foreground">
              Offen: {summary.open_count} · netto voraussichtlich {formatCurrency(summary.open_net)}
            </div>
          </div>
        )}

        {data?.seller_tax_id_missing && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            Im Firmenstamm fehlen Steuernummer und USt-IdNr. – Entwürfe können angelegt, aber nicht ausgestellt werden (X10).
          </div>
        )}

        {loading && !data && <p className="text-muted-foreground text-center py-8">Laden...</p>}
        {data && suggestions.length === 0 && invoices.length === 0 && (
          <p className="text-muted-foreground text-center py-8">Für {data.month_label} ist nichts offen.</p>
        )}

        {/* Block 1: Monatsabrechnung */}
        {data && (monthRows.length > 0 || carryoverRows.length > 0) && (
          <section className="space-y-2">
            <div className="flex items-baseline gap-2">
              <h2 className="font-semibold text-foreground">Monatsabrechnung {data.month_label}</h2>
              <span className="text-xs text-muted-foreground">Aufträge mit monatlicher Abrechnung – alle Buchungen des Monats</span>
            </div>
            <div className="bg-card/50 border border-border rounded-lg overflow-x-auto">
              <div className="min-w-[980px]">
                {tableHead}
                {monthRows.map(renderRow)}
                {monthRows.length === 0 && (
                  <p className="text-sm text-muted-foreground px-4 py-3">Für {data.month_label} ist nichts mehr offen.</p>
                )}
                {carryoverRows.length > 0 && (
                  <div className="border-t border-border">
                    <button
                      type="button"
                      onClick={() => setCarryoverOpen(!carryoverOpen)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-300 hover:bg-muted/40"
                      aria-expanded={carryoverOpen}
                    >
                      {carryoverOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      Offen aus früheren Monaten ({carryoverRows.length}) · netto {formatCurrency(carryoverRows.reduce((t, s) => t + (s.net_total || 0), 0))}
                    </button>
                    {carryoverOpen && <div className="border-t border-border">{carryoverRows.map(renderRow)}</div>}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Block 2: Nach Leistung */}
        {data && completionRows.length > 0 && (
          <section className="space-y-2">
            <div className="flex items-baseline gap-2">
              <h2 className="font-semibold text-foreground">Nach Leistung</h2>
              <span className="text-xs text-muted-foreground">alle offenen Buchungen je Auftrag – fertige Teilaufgaben herausgreifen, der Rest bleibt offen</span>
            </div>
            <div className="bg-card/50 border border-border rounded-lg overflow-x-auto">
              <div className="min-w-[980px]">
                {tableHead}
                {completionRows.map(renderRow)}
              </div>
            </div>
          </section>
        )}

        {/* Bereits angelegt */}
        {invoices.length > 0 && (
          <section className="space-y-2">
            <h2 className="font-semibold text-foreground">Angelegt mit Leistungszeitraum in {data?.month_label}</h2>
            <div className="bg-card/50 border border-border rounded-lg overflow-x-auto">
              <div className="min-w-[980px]">
                {invoices.map((inv) => (
                  <div key={inv.id} className={`grid ${GRID} gap-3 px-4 py-3 items-center border-b border-border last:border-b-0 bg-muted/20`}>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{inv.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{inv.project_names ? `${inv.project_names} · ` : ''}{formatDate(inv.period_start)} – {formatDate(inv.period_end)} · {inv.invoice_number || inv.draft_reference}</p>
                    </div>
                    <div className="text-sm text-muted-foreground col-span-3">angelegt</div>
                    <div className="font-mono text-sm">{formatCurrency(inv.net_total)}</div>
                    <div>{getStatusBadge(inv)}</div>
                    <div className="flex justify-end">
                      <Button type="button" size="sm" variant="outline" className="border-border" onClick={() => onOpenInvoice(inv.id)}>
                        Öffnen
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <p className="text-xs text-muted-foreground">
          Die Abrechnungsart steht am Auftrag (Reiter „Abrechnung“), sonst gilt die Einstellung des Kunden bzw. der Firmenstandard. Beträge sind ein Probelauf mit den heute gültigen Sätzen – genau wie beim Anlegen. Buchungen aus nicht genehmigten Wochen sind ein Hinweis, keine Sperre. Fahrten ohne Auftrag gehören zum einzigen Auftrag des Kunden im Monat, sonst stehen sie in einer eigenen Zeile.
        </p>

        {/* Auswahl: Teilaufgaben nach Tätigkeit bzw. einzelne Buchungen */}
      </div>

      <Sheet open={!!panel} onOpenChange={(open) => { if (!open) setPanel(null); }}>
        <SheetContent className="bg-card border-border w-full sm:max-w-lg flex flex-col">
          {panel && (
            <>
              <SheetHeader>
                <SheetTitle className="text-foreground">{panel.suggestion.mode === 'none' ? 'Teilaufgaben wählen' : 'Auswahl anpassen'}</SheetTitle>
                <SheetDescription>
                  {panel.suggestion.customer_name} · {panel.suggestion.label}
                  {panel.suggestion.mode === 'none'
                    ? ' – fertige Teilaufgaben abhaken; was nicht gewählt ist, bleibt offen.'
                    : ' – nur für Buchungen, die ausdrücklich nicht auf diese Rechnung sollen.'}
                </SheetDescription>
              </SheetHeader>
              <div className="flex items-center gap-2 py-3 border-b border-border text-sm">
                <Button type="button" variant="outline" size="sm" className="border-border"
                  onClick={() => setPanel({ ...panel, entries: new Set(panel.suggestion.entries.map((e) => e.id)), mileage: new Set(panel.suggestion.mileage.map((m) => m.id)) })}>
                  Alle
                </Button>
                <Button type="button" variant="outline" size="sm" className="border-border"
                  onClick={() => setPanel({ ...panel, entries: new Set(), mileage: new Set() })}>
                  Keine
                </Button>
                <span className="ml-auto text-muted-foreground">
                  {panel.entries.size} von {panel.suggestion.entries.length} Buchungen
                  {panel.suggestion.mileage.length ? ` · ${panel.mileage.size} von ${panel.suggestion.mileage.length} Fahrten` : ''}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto">
                {panelGroups.map((g) => {
                  const ids = g.entries.map((e) => e.id);
                  const on = ids.filter((id) => panel.entries.has(id)).length;
                  const allOn = on === ids.length;
                  const hours = g.entries.filter((e) => !e.is_daily_rate).reduce((t, e) => t + (e.billed_hours ?? e.hours ?? 0), 0);
                  const days = g.entries.filter((e) => e.is_daily_rate).length;
                  return (
                    <div key={g.label} className="border-b border-border">
                      <label className="flex items-center gap-3 py-2 px-1 cursor-pointer bg-muted/30">
                        <Checkbox checked={allOn ? true : on > 0 ? 'indeterminate' : false} onCheckedChange={() => toggleGroup(ids, allOn)} aria-label={`Tätigkeit ${g.label}`} />
                        <span className="flex-1 font-medium text-foreground">{g.label}</span>
                        <span className="text-xs text-muted-foreground">{plural(ids.length, 'Buchung', 'Buchungen')}</span>
                        <span className="font-mono text-sm">{days ? plural(days, 'Tag', 'Tage') : hoursText(hours)}</span>
                      </label>
                      {g.entries.map((e) => (
                        <label key={e.id} className="flex items-start gap-3 py-2 pl-7 pr-1 cursor-pointer text-sm border-t border-border/60">
                          <Checkbox checked={panel.entries.has(e.id)} onCheckedChange={() => togglePanel('entries', e.id)} className="mt-0.5" />
                          <span className="w-20 text-muted-foreground">{formatDate(e.date)}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-foreground truncate">{e.description || e.project_name}</span>
                            <span className="block text-xs text-muted-foreground">
                              {e.user_name}
                              {e.unapproved && <span className="text-amber-600 dark:text-amber-400"> · Woche nicht genehmigt</span>}
                            </span>
                          </span>
                          <span className="font-mono">
                            {e.is_daily_rate ? 'Tagessatz' : hoursText(e.billed_hours ?? e.hours)}
                          </span>
                        </label>
                      ))}
                    </div>
                  );
                })}
                {panel.suggestion.mileage.length > 0 && (
                  <div className="border-b border-border">
                    <div className="py-2 px-1 bg-muted/30 font-medium text-foreground text-sm">Fahrten</div>
                    {panel.suggestion.mileage.map((m) => (
                      <label key={m.id} className="flex items-start gap-3 py-2 pl-7 pr-1 cursor-pointer text-sm border-t border-border/60">
                        <Checkbox checked={panel.mileage.has(m.id)} onCheckedChange={() => togglePanel('mileage', m.id)} className="mt-0.5" />
                        <span className="w-20 text-muted-foreground">{formatDate(m.date)}</span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-foreground truncate">{m.description || 'Fahrt'}</span>
                          <span className="block text-xs text-muted-foreground">{m.user_name}{m.project_name ? ` · ${m.project_name}` : ''}</span>
                        </span>
                        <span className="font-mono">{(m.kilometers || 0).toLocaleString('de-DE')} km</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {referenceNeeded(panel.suggestion) && (
                <div className="py-3 border-t border-border flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">{REFERENCE_LABELS[panel.suggestion.reference.type] || 'Referenz'} (Pflicht):</span>
                  <Input
                    value={references[panel.suggestion.key] || ''}
                    onChange={(e) => setReferences({ ...references, [panel.suggestion.key]: e.target.value })}
                    placeholder="Erforderlich"
                    className="h-8 flex-1 bg-input border-border text-foreground"
                  />
                </div>
              )}
              <SheetFooter className="pt-3 border-t border-border gap-2">
                <Button type="button" variant="outline" onClick={() => setPanel(null)}>Abbrechen</Button>
                <Button
                  type="button"
                  className="bg-cyan-600 hover:bg-cyan-700"
                  disabled={(panel.entries.size + panel.mileage.size) === 0 || creating === panel.suggestion.key || (referenceNeeded(panel.suggestion) && !referenceInput(panel.suggestion))}
                  onClick={() => createDraft(panel.suggestion, [...panel.entries], [...panel.mileage])}
                >
                  {creating === panel.suggestion.key ? 'Erstelle...' : 'Entwurf mit Auswahl anlegen'}
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
